import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isNil } from 'lodash-es';
import classnames from 'classnames';
import { RobotTotalCleanedAreaChartBar } from '../RobotTotalCleanedAreaChartBar/RobotTotalCleanedAreaChartBar';
import { StyledRobotTotalCleanedAreaChartElement } from './RobotTotalCleanedAreaChartElement.styles';
import {
  Maybe,
  RobotDashboardTotalCleanedAreaData,
} from 'app/cross-cutting-concerns/communication/interfaces/am-api-graphql';
import { ConvertUnitUtils } from 'app/utils/convert-unit/ConvertUnitUtils';
import { NoStyleButton } from 'lib/components/Button/NoStyleButton/NoStyleButton';
import { buildMachineDetailsTabPath } from 'app/modules/machine-inventory/machine-details-panel/hooks/useMachineDetailsTabFromUrl';
import { RoutePaths } from 'config/route-paths';

export interface IRobotTotalCleanedAreaChartElementProps {
  className: string;
  keyIndex: string;
  totalCleanedAreasDatum: RobotDashboardTotalCleanedAreaData;
  cleanedAreasMaximum: number;
}

export const RobotTotalCleanedAreaChartElement = ({
  className,
  keyIndex,
  totalCleanedAreasDatum,
  cleanedAreasMaximum,
}: IRobotTotalCleanedAreaChartElementProps): Maybe<JSX.Element> => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isUSusers = false;

  if (!totalCleanedAreasDatum.machineName || isNil(totalCleanedAreasDatum) || isNil(cleanedAreasMaximum)) {
    return null;
  }

  const { machineId, machineName } = totalCleanedAreasDatum;

  return (
    <StyledRobotTotalCleanedAreaChartElement
      className={classnames('robot-total-cleaned-area-chart-element', className)}
    >
      <div className="robot-total-cleaned-area-chart-element__name" title={machineName}>
        <NoStyleButton
          className={classnames(
            'robot-total-cleaned-area-chart-element__link',
            'robot-total-cleaned-area-chart-element__link--no-styling'
          )}
          onClick={(): void => {
            navigate(buildMachineDetailsTabPath(machineId, 'CLEANING_DATA', RoutePaths.ROBOT_DASHBOARD));
          }}
        >
          {machineName}
        </NoStyleButton>
      </div>
      {isUSusers ? (
        <>
          <div className="robot-total-cleaned-area-chart-element__bar">
            <RobotTotalCleanedAreaChartBar
              key={keyIndex}
              totalCleanedAreasDatum={totalCleanedAreasDatum}
              cleanedAreasMaximum={ConvertUnitUtils.covertSquareMetersToSquareFeet(cleanedAreasMaximum)}
            />
          </div>
          <div
            className="robot-total-cleaned-area-chart-element__cleaned-area"
            title={totalCleanedAreasDatum.machineName}
          >
            {ConvertUnitUtils.covertSquareMetersToSquareFeet(totalCleanedAreasDatum.cleanedArea)}&#160;
            {t('common.areaUnitFeetSquare')}
          </div>
        </>
      ) : (
        <>
          <div className="robot-total-cleaned-area-chart-element__bar">
            <RobotTotalCleanedAreaChartBar
              key={keyIndex}
              totalCleanedAreasDatum={totalCleanedAreasDatum}
              cleanedAreasMaximum={cleanedAreasMaximum}
            />
          </div>
          <div
            className="robot-total-cleaned-area-chart-element__cleaned-area"
            title={totalCleanedAreasDatum.machineName}
          >
            {totalCleanedAreasDatum.cleanedArea}&#160;{t('robotDashboard.squareUnit')}
          </div>
        </>
      )}
    </StyledRobotTotalCleanedAreaChartElement>
  );
};
